import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { classNames } from "primereact/utils";
import { Divider } from "primereact/divider";
import { object, string, ref, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import loadingIcon from "../icons/loading-icon.svg";
import googleIcon from "../icons/google-icon.svg";
import facebookIcon from "../icons/facebook-icon.svg";
import appleIcon from "../icons/apple-icon.svg";

const TrainerBookingPopup = ({ visible, onHide, text }) => {
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [showLoading, setShowLoading] = useState(false);

  const handleHide = () => {
    if (!visible) return;
    onHide();
    setShowLoginForm(true);
    setShowLoading(false);
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "37.5rem" }}
      onHide={handleHide}
      pt={{
        header: {
          class:
            "bg-white p-3 pb-3 md:pt-4 md:px-4 flex justify-content-end border-round-top-lg",
        },
        content: {
          class:
            "bg-white px-3 md:px-5 pb-4 border-round-bottom-lg overflow-y-auto",
        },
      }}
      className="border-round-lg"
      breakpoints={{ "960px": "85vw", "641px": "95vw" }}
      blockScroll={true}
      baseZIndex="10001"
    >
      <div className="flex flex-column gap-4">
        {showLoginForm ? (
          <LoginForm
            onHide={handleHide}
            showResisterForm={() => setShowLoginForm(false)}
            showLoading={(value) => setShowLoading(value)}
            text={text}
          />
        ) : (
          <RegisterForm
            onHide={handleHide}
            showLoginForm={() => setShowLoginForm(true)}
            showLoading={(value) => setShowLoading(value)}
            text={text}
          />
        )}
      </div>
      {showLoading ? (
        <div
          className="absolute top-0 left-0 border-round-lg blur-md w-full h-full z-5 flex justify-content-center align-items-center"
          style={{
            background: "rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(3px)",
          }}
        >
          <img src={loadingIcon} alt="loading" className="rotating" />
        </div>
      ) : null}
    </Dialog>
  );
};

const LoginForm = ({ onHide, showResisterForm, showLoading, text }) => {
  const defaultValues = {
    email: "",
    password: "",
  };
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm({ defaultValues });

  const onSubmit = (data) => {
    showLoading(true);
    setTimeout(() => {
      showLoading(false);
    }, 1000);
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  return (
    <div className="flex flex-column" style={{ gap: "1.25rem" }}>
      <span className="text-2xl font-bold">{text.loginTitle}</span>

      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
        <div className="field">
          <div className="flex flex-column gap-2">
            <label
              htmlFor="email"
              className={classNames({
                "p-error": !!errors.email,
                "text-sm": true,
              })}
            >
              {text.emailLabel} *
            </label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address. E.g. example@email.com",
                },
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  className={classNames({
                    "p-invalid": fieldState.invalid,
                    "p-inputtext-lg": true,
                    "text-base": true,
                  })}
                  placeholder={text.emailPlaceholder}
                />
              )}
            />
          </div>
          {getFormErrorMessage("email")}
        </div>
        <div className="field">
          <div className="flex flex-column gap-2">
            <label
              htmlFor="password"
              className={classNames({
                "p-error": errors.password,
                "text-sm": true,
              })}
            >
              {text.passwordLabel} *
            </label>
            <Controller
              name="password"
              control={control}
              rules={{ required: "Password is required." }}
              render={({ field, fieldState }) => (
                <Password
                  id={field.name}
                  {...field}
                  className="text-base"
                  pt={{ input: { style: { padding: "0.625rem 0.625rem" } } }}
                  placeholder={text.passwordPlaceholder}
                  toggleMask
                  feedback={false}
                  invalid={fieldState.invalid}
                />
              )}
            />
          </div>
          {getFormErrorMessage("password")}
        </div>
        <Divider align="center" className="mb-0 mt-5">
          <span>{text.signInWith}</span>
        </Divider>

        <div className="w-full flex gap-3 my-4 flex-wrap md:flex-nowrap">
          {loginWith.map((item) => {
            return (
              <Button
                label={item.label}
                icon={
                  <img
                    src={item.icon}
                    className="border-right-1 border-gray-400"
                    style={{ padding: "0.75rem" }}
                    alt={item.label}
                  />
                }
                severity="secondary"
                className="w-full p-0 border-gray-400"
                outlined
                pt={{
                  label: {
                    class: "text-center w-full text-black-alpha-90",
                  },
                }}
              />
            );
          })}
        </div>

        <Divider className="m-0" />

        <div className="text-sm text-center w-full my-3">
          <span onClick={showResisterForm} className="cursor-pointer">
            {text.registerNow}
          </span>
        </div>

        <Divider className="m-0" />

        <div className="flex w-full justify-content-between mt-4 flex-column-reverse md:flex-row gap-2">
          <Button
            label={text.skipBtn}
            className="w-full md:w-11rem"
            outlined
            style={{ padding: "0.75rem 0" }}
            onClick={onHide}
          />
          <Button
            type="submit"
            label={text.loginBtn}
            className="w-full md:w-11rem"
            disabled={!isDirty}
            style={{ padding: "0.75rem 0" }}
          />
        </div>
      </form>
    </div>
  );
};

const RegisterForm = ({ onHide, showLoginForm, showLoading, text }) => {
  const [registerSucces, setRegisterSucces] = useState(false);
  const formSchema = object().shape({
    firstName: string(),
    lastName: string(),
    email: string()
      .email("Invalid email address. E.g. example@email.com")
      .required("Email is required"),
    password: string()
      .required("Password is required")
      .min(4, "Password length should be at least 4 characters"),
    confirmPassword: string()
      .required("Confirm Password is required")
      .min(4, "Password length should be at least 4 characters")
      .oneOf([ref("password")], "Passwords do not match"),
    accept: boolean(),
  });

  const defaultValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    accept: false,
  };
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm({ defaultValues, resolver: yupResolver(formSchema) });

  const onSubmit = (data) => {
    showLoading(true);
    setTimeout(() => {
      showLoading(false);
      setRegisterSucces(true);
    }, 1000);
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  if (registerSucces) {
    return (
      <div
        className="flex flex-column align-items-center"
        style={{ gap: "1.25rem" }}
      >
        <p className="text-2xl font-bold text-center m-0 flex flex-column">
          <span>{text.congratulationsTitle}</span>
          <span>{text.congratulationsSub}</span>
        </p>
        <Button
          label={text.loginBtn}
          className="w-10rem"
          style={{ padding: "0.75rem 0" }}
          onClick={showLoginForm}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-column" style={{ gap: "1.25rem" }}>
      <span className="text-2xl font-bold">{text.signupTitle}</span>
      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
        <div className="field">
          <div className="flex flex-column gap-2">
            <label className="text-sm">{text.fullNameLabel}</label>
            <div className="flex gap-3">
              <Controller
                name="firstName"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    className={classNames({
                      "p-invalid": fieldState.invalid,
                      "p-inputtext-lg": true,
                      "text-base": true,
                    })}
                    placeholder={text.fristNamePlaceholder}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    className={classNames({
                      "p-invalid": fieldState.invalid,
                      "p-inputtext-lg": true,
                      "text-base": true,
                    })}
                    placeholder={text.lastnamePlaceholder}
                  />
                )}
              />
            </div>
            {getFormErrorMessage("lastName")}
          </div>
        </div>
        <div className="field">
          <div className="flex flex-column gap-2">
            <label
              htmlFor="email"
              className={classNames({
                "p-error": !!errors.email,
                "text-sm": true,
              })}
            >
              {text.emailLabel} *
            </label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address. E.g. example@email.com",
                },
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  className={classNames({
                    "p-invalid": fieldState.invalid,
                    "p-inputtext-lg": true,
                    "text-base": true,
                  })}
                  placeholder={text.emailPlaceholder}
                />
              )}
            />
          </div>
          {getFormErrorMessage("email")}
        </div>
        <div className="flex gap-3">
          <div className="field w-full">
            <div className="flex flex-column gap-2">
              <label
                htmlFor="password"
                className={classNames({
                  "p-error": errors.password,
                  "text-sm": true,
                })}
              >
                {text.passwordLabel} *
              </label>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <Password
                    id={field.name}
                    {...field}
                    className="text-base"
                    pt={{ input: { style: { padding: "0.625rem 0.625rem" } } }}
                    placeholder={text.passwordPlaceholder}
                    toggleMask
                    feedback={false}
                    invalid={fieldState.invalid}
                  />
                )}
              />
            </div>
            {getFormErrorMessage("password")}
          </div>
          <div className="field w-full">
            <div className="flex flex-column gap-2">
              <label
                htmlFor="confirmPassword"
                className={classNames({
                  "p-error": errors.confirmPassword,
                  "text-sm": true,
                })}
              >
                {text.confirmPasswordLabel} *
              </label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <Password
                    id={field.name}
                    {...field}
                    className="text-base"
                    pt={{ input: { style: { padding: "0.625rem 0.625rem" } } }}
                    placeholder={text.confirmPasswordPlaceholder}
                    toggleMask
                    feedback={false}
                    invalid={fieldState.invalid}
                  />
                )}
              />
            </div>
            {getFormErrorMessage("confirmPassword")}
          </div>
        </div>
        <div className="field-checkbox">
          <Controller
            name="accept"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Checkbox
                inputId={field.name}
                onChange={(e) => field.onChange(e.checked)}
                checked={field.value}
              />
            )}
          />
          <label
            htmlFor="accept"
            className={classNames({
              "p-error": errors.accept,
              "text-sm": true,
            })}
          >
            {text.termsandServices}
          </label>
        </div>

        <Divider align="center" className="mb-0 mt-5">
          <span>Register an account with</span>
        </Divider>
        <div className="w-full flex gap-3 my-4 flex-wrap md:flex-nowrap">
          {loginWith.map((item) => {
            return (
              <Button
                label={item.label}
                icon={
                  <img
                    src={item.icon}
                    className="border-right-1 border-gray-400"
                    style={{ padding: "0.75rem" }}
                    alt={item.label}
                  />
                }
                severity="secondary"
                className="w-full p-0 border-gray-400"
                outlined
                pt={{
                  label: {
                    class: "text-center w-full text-black-alpha-90",
                  },
                }}
              />
            );
          })}
        </div>
        <Divider className="m-0" />

        <div className="text-sm text-center w-full my-3">
          <span onClick={showLoginForm} className="cursor-pointer">
            {text.signInNow}
          </span>
        </div>

        <Divider className="m-0" />

        <div className="flex w-full justify-content-between mt-4 flex-column-reverse md:flex-row gap-2">
          <Button
            label={text.skipBtn}
            className="w-full md:w-11rem"
            outlined
            onClick={onHide}
            style={{ padding: "0.75rem 0" }}
          />
          <Button
            type="submit"
            label={text.signupBtn}
            className="w-full md:w-11rem"
            disabled={!isDirty}
            style={{ padding: "0.75rem 0" }}
          />
        </div>
      </form>
    </div>
  );
};

const loginWith = [
  {
    label: "Google",
    icon: googleIcon,
  },
  {
    label: "Facebook",
    icon: facebookIcon,
  },
  {
    label: "Apple",
    icon: appleIcon,
  },
];

export default TrainerBookingPopup;
